/**
 * Plain-language copy for the grounding surfaces (the report hover card and
 * the validation review drawer rows).
 *
 * Before this module both surfaces led with the checker's own rationale and
 * raw verdict chips ("OVERSTATED", "NEW · DEEP CHECK", "UNRETRIEVED
 * IDENTIFIER") — accurate, but it told a reviewer nothing about what the
 * colour means or what a button would do to their report. The product
 * decision: every card leads with one plain line for the colour and one
 * plain line per button; the checker's rationale and the chips stay
 * available underneath a collapsed "Details" disclosure.
 *
 * The copy lives here, in one place, so the card and the drawer cannot
 * drift apart and the strings are assertable from tests without mounting
 * either component.
 */

export interface GroundingCardCopy {
  /** One line saying what the colour means. */
  headline: string;
  /** One line of supporting detail. Absent on post-decision headlines. */
  subline?: string;
}

/** Last line of every card: the checks never block acceptance, but any
 *  button re-runs them. */
export const GROUNDING_CARD_FOOTER =
  'Nothing here blocks acceptance. Any button means the check runs again before you can accept.';

/** One line per button, saying what pressing it does. Keyed by the button,
 *  not by the card state — the same button means the same thing wherever it
 *  renders (including its "Fix again" / "Search again" retry variants). */
export const GROUNDING_ACTION_LINES = {
  fix: 'Fix: the agent rewrites this sentence to match the source. You do not review the new text first.',
  reject: 'Reject: keep the sentence as is. Logged as your decision, nothing else changes.',
  findSource: 'Find source: the agent searches for this regulation and attaches it if found.',
  internal: "Internal: mark as the bank's own knowledge. Drops it from the checks.",
} as const;

/** After a decision the headline is replaced (the details fold stays).
 *  `accepted` and `good_enough` are deliberately absent — they keep the
 *  labels that already exist on those surfaces rather than gaining a new
 *  state. */
export const GROUNDING_DECISION_HEADLINES: Record<string, string> = {
  rejected: 'Dismissed by you.',
  fix_requested: 'Fix requested, agent working.',
  source_requested: 'Source requested, agent searching.',
  accepted_internal: 'Marked internal.',
};

const RED_OVERSTATED: GroundingCardCopy = {
  headline: 'Says more than its source.',
  subline: 'The cited text supports a narrower statement.',
};

const RED_CONTRADICTED: GroundingCardCopy = {
  headline: 'Disagrees with its source.',
  subline: 'The cited text says something different.',
};

const AMBER_UNVERIFIABLE: GroundingCardCopy = {
  headline: 'Source not found.',
  subline: 'BARK never retrieved this text, so it cannot check the sentence.',
};

const GREEN_SUPPORTED: GroundingCardCopy = {
  headline: 'Matches its source.',
  subline: 'No action needed.',
};

/**
 * Resolve the plain-language headline for one finding / drawer row.
 *
 * A recorded decision wins (the card then says what the agent is doing
 * rather than what the checker found). Otherwise severity picks the colour
 * and the verdict refines the red wording.
 *
 * Returns null where there is no agreed copy for the state — a pre-run
 * amber (nothing has been checked yet, so the existing "not yet
 * semantically checked" framing still applies) and a red carrying a verdict
 * outside the agreed set. Callers render no headline in that case rather
 * than inventing one.
 */
export function groundingCardCopy({
  severity,
  verdict,
  decision,
}: {
  severity?: 'confirmed' | 'cleared' | 'suspected';
  verdict?: string;
  decision?: string;
}): GroundingCardCopy | null {
  if (decision && GROUNDING_DECISION_HEADLINES[decision]) {
    return { headline: GROUNDING_DECISION_HEADLINES[decision] };
  }
  if (severity === 'cleared') return GREEN_SUPPORTED;
  if (severity === 'confirmed') {
    if (verdict === 'overstated') return RED_OVERSTATED;
    if (verdict === 'contradicted') return RED_CONTRADICTED;
    return null;
  }
  // Amber. An adjudicated amber is one the judge saw but could not rule on;
  // a bare amber has not been through semantic validation at all.
  if (severity === 'suspected' && verdict) return AMBER_UNVERIFIABLE;
  return null;
}
